import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Models
import {
  IAppointmentSettings,
  ICorrespondenceTimes,
  IPrebookedTimes,
  ISessionAvailableAttributes,
  IStoreSession,
  IStoreSessionAttributes
} from '../../interfaces/session-settings.interfaces';
import { TSessionType } from '../../types/settings.types';

export interface ISessionSettingsState {
  attributes: ISessionAvailableAttributes,
  sessions: IStoreSessionAttributes,
  appointmentsSettings: IAppointmentSettings | null,
  booked: IPrebookedTimes,
  correspondence: ICorrespondenceTimes,
  loading: boolean,
  error: string | null,
}

const initialState: ISessionSettingsState = {
  attributes: {
    audio_call_duration: [],
    audio_call_buffer: [],
    video_call_buffer: [],
    video_call_duration: [],
    conversation_buffer: [],
    conversation_duration: [],
  },
  sessions: {
    audio_call_session: [],
    video_call_session: [],
    conversation_session: [],
  },
  appointmentsSettings: null,
  booked: {
    available_call_prebooked_time: [],
    call_prebooked_time: 0,
    call_socket_time: 0,
  },
  correspondence: {
    correspondence_available_time: [],
    correspondence_message_time: 0,
  },
  loading: false,
  error: null
};

export const sessionSettingsSlice = createSlice({
  name: 'session-settings',
  initialState,
  reducers: {
    getSessionAvailableAttributesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getSessionAvailableAttributesRequestSuccess: (state, { payload: attributes }: PayloadAction<ISessionAvailableAttributes>) => {
      state.attributes = attributes;
      state.loading = false;
      state.error = null;
    },
    getSessionAvailableAttributesRequestFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    getAppointmentSettingsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getAppointmentSettingsRequestSuccess: (state, { payload: appointmentsSettings }: PayloadAction<IAppointmentSettings>) => {
      state.appointmentsSettings = appointmentsSettings;
      state.loading = false;
      state.error = null;
    },
    getAppointmentSettingsRequestFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    updateAppointmentsSettings: (state, { payload: appointmentsSettings }: PayloadAction<IAppointmentSettings>) => {
      state.appointmentsSettings = appointmentsSettings;
    },
    getStoreSessionAttributesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getStoreSessionAttributesRequestSuccess: (state, { payload: sessions }: PayloadAction<IStoreSessionAttributes>) => {
      state.sessions = sessions;
      state.loading = false;
      state.error = null;
    },
    getStoreSessionAttributesRequestFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    getAvailableCallPrebookedTimeRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getAvailableCallPrebookedTimeRequestSuccess: (state, { payload: booked }: PayloadAction<IPrebookedTimes>) => {
      state.booked = booked;
      state.loading = false;
      state.error = null;
    },
    getAvailableCallPrebookedTimeRequestFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    getAvailableCorrespondenceTimeRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getAvailableCorrespondenceTimeRequestSuccess: (state, { payload: correspondence }: PayloadAction<ICorrespondenceTimes>) => {
      state.correspondence = correspondence;
      state.loading = false;
      state.error = null;
    },
    getAvailableCorrespondenceTimeRequestFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    createStoreSessionAttributes: (state, { payload: session }: PayloadAction<IStoreSession>) => {
      let sessionKey: keyof typeof state.sessions;
      switch (session.session_type) {
        case 'audio_call':
          sessionKey = 'audio_call_session';
          break;
        case 'video_call':
          sessionKey = 'video_call_session';
          break;
        case 'conversation':
          sessionKey = 'conversation_session';
          break;
        default:
          sessionKey = 'video_call_session';
      }
      state.sessions = {
        ...state.sessions,
        [sessionKey]: [...state.sessions[sessionKey], session]
      };
      state.loading = false;
      state.error = null;
    },
    updateStoreSessionAttributes: (state, { payload: session }: PayloadAction<IStoreSession>) => {
      let sessionKey: keyof typeof state.sessions;
      switch (session.session_type) {
        case 'audio_call':
          sessionKey = 'audio_call_session';
          break;
        case 'video_call':
          sessionKey = 'video_call_session';
          break;
        case 'conversation':
          sessionKey = 'conversation_session';
          break;
        default:
          sessionKey = 'video_call_session';
      }
      state.sessions = {
        ...state.sessions,
        [sessionKey]: state.sessions[sessionKey].map(sSession => sSession.id === session.id ? session : sSession)
      };
      state.loading = false;
      state.error = null;
    },
    deleteStoreSessionAttributes: (state, { payload: { id, session_type } }: PayloadAction<{
      id: number | string,
      session_type: TSessionType
    }>) => {
      let sessionKey: keyof typeof state.sessions;
      switch (session_type) {
        case 'audio_call':
          sessionKey = 'audio_call_session';
          break;
        case 'video_call':
          sessionKey = 'video_call_session';
          break;
        case 'conversation':
          sessionKey = 'conversation_session';
          break;
        default:
          sessionKey = 'video_call_session';
      }
      state.sessions = {
        ...state.sessions,
        [sessionKey]: state.sessions[sessionKey].filter(session => session.id !== id)
      };
      state.loading = false;
      state.error = null;
    },
    clearSessionSettings: (state) => {
      state.attributes = {
        audio_call_duration: [],
        audio_call_buffer: [],
        video_call_buffer: [],
        video_call_duration: [],
        conversation_buffer: [],
        conversation_duration: [],
      };
      state.sessions = {
        audio_call_session: [],
        video_call_session: [],
        conversation_session: [],
      };
      state.appointmentsSettings = null;
      state.loading = true;
      state.error = null;
    },
  }
});

export const SessionSettingsSliceActions = sessionSettingsSlice.actions;
